// @givmo/board — the fine print, kept one click away (CBG-UX1, Jonathan's "say less").
//
// Each Board page and card says ONE short line; the sentences that explain how it works sit behind a
// quiet "How this works" link, and a single fact's full wording sits behind an info glyph beside it.
// Both open a popover in the SaaS menu's skin (`Popover.Content saas`), so the fine print reads as the
// same system as the menus beside it.
//
// ⚠ WHAT GOES BEHIND THEM IS STILL THE PRODUCT'S VOICE, and the §3.2/§3.4 discipline binds it exactly
// as it binds the page: arithmetic and mechanics, never a verdict. The info tip on a meeting's notice
// carries the backend's own sentence VERBATIM — never a paraphrase — because that sentence is the one
// written to state the count without concluding anything from it.

import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Icon, Popover, cn, testIdProp } from '@givmo/design-system';

/** The popover's own box: width, inset and the viewport gutter it keeps on a phone. */
const POPOVER_BOX = 'w-[320px] max-w-[calc(100vw-32px)] p-16';

/** The fine print's type: small, secondary, generous line height. */
const FINE_PRINT = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';

export interface HowThisWorksProps {
  /** Two to four short sentences, each its own line. */
  lines: readonly ReactNode[];
  /** The trigger's testid; the popover takes `<testId>-popover`. */
  testId: string;
  /** The link's words. */
  label?: string;
  align?: 'start' | 'center' | 'end';
  /** The link's own size where it closes a smaller line (`text-s12`); the popover keeps its own. */
  className?: string;
}

/** A section's fine print, behind a small "How this works" link. */
export function HowThisWorks({
  lines,
  testId,
  label = 'How this works',
  align = 'start',
  className,
}: HowThisWorksProps) {
  return (
    <Popover.Root>
      <Popover.Trigger
        className={cn(
          'inline font-sans text-s13 font-w600 tracking-givmo text-brand-600 underline-offset-2 outline-none transition-colors hover:text-brand-700 hover:underline focus-visible:underline',
          className,
        )}
        {...testIdProp(testId)}
      >
        {label}
      </Popover.Trigger>
      <Popover.Content
        saas
        side="bottom"
        align={align}
        sideOffset={8}
        collisionPadding={16}
        aria-label={label}
        className={POPOVER_BOX}
        testId={`${testId}-popover`}
      >
        <ul className="flex flex-col gap-10">
          {lines.map((line, index) => (
            <li key={index} className={FINE_PRINT}>
              {line}
            </li>
          ))}
        </ul>
      </Popover.Content>
    </Popover.Root>
  );
}

export interface InfoTipProps {
  /** The trigger's accessible name, e.g. "How the notice count is worked out". */
  label: string;
  children: ReactNode;
  /** The trigger's testid; the popover takes `<testId>-popover`. */
  testId: string;
}

/**
 * One fact's full wording, behind an info glyph beside it. Rendered INLINE so the glyph follows the
 * fact's last word wherever the line wraps, rather than dropping onto a line of its own.
 */
export function InfoTip({ label, children, testId }: InfoTipProps) {
  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={label}
        className="-my-2 inline-flex size-20 shrink-0 items-center justify-center rounded-sm align-middle text-icon-muted outline-none transition-colors hover:bg-surface-hover hover:text-icon focus-visible:shadow-ring"
        {...testIdProp(testId)}
      >
        <Icon icon={Info} size={15} aria-hidden />
      </Popover.Trigger>
      <Popover.Content
        saas
        side="bottom"
        align="start"
        sideOffset={6}
        collisionPadding={16}
        aria-label={label}
        className={POPOVER_BOX}
        testId={`${testId}-popover`}
      >
        <div className={FINE_PRINT}>{children}</div>
      </Popover.Content>
    </Popover.Root>
  );
}

export type StatusWordTone = 'plain' | 'act' | 'quiet';

export interface StatusWordProps {
  children: ReactNode;
  /**
   * `plain` — an ordinary state ("Draft", "Imported", "In force", "Coming").
   * `act` — the ONE coloured tone, for a state someone must act on ("Awaiting approval").
   * `quiet` — an absence ("No reply yet").
   */
  tone?: StatusWordTone;
  testId?: string;
}

/**
 * A status as a plain word — no badge, no pill. Colour appears only where someone must act, and no
 * word restates the default (there is no "Scheduled" and no "Regular").
 */
export function StatusWord({ children, tone = 'plain', testId }: StatusWordProps) {
  return (
    <span
      className={cn(
        'whitespace-nowrap font-sans text-s13 tracking-givmo',
        tone === 'act' && 'font-w600 text-warning-700',
        tone === 'plain' && 'font-w500 text-text-secondary',
        tone === 'quiet' && 'font-w400 text-text-tertiary',
      )}
      data-tone={tone}
      {...testIdProp(testId)}
    >
      {children}
    </span>
  );
}

/** The quiet caption above a group of rows ("Upcoming", "Past", "Not yet approved"). */
export function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
      {children}
    </h2>
  );
}
